import React, { useEffect, useState, useCallback, useContext, Fragment } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import {
  Container,
  Box,
  Text,
  Flex,
  Spacer,
  Center,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Stack,
  HStack,
  Tooltip,
  useDisclosure,
  IconButton,
  useBreakpointValue,
  TableContainer,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  // VStack,
} from "@chakra-ui/react";

import { fetchAuthSession } from 'aws-amplify/auth';

// decorative components
import { Divider } from '../Divider.jsx';

// modals
import { CenteredStatusProgressModal } from '../Modal/CenteredStatusProgressModal.jsx';

// buttons
import { ButtonSenaryWithIconOnly } from '../Form/Button/ButtonSenaryWithIconOnly.jsx';
import { ButtonQuaternaryWithIcon } from '../Form/Button/ButtonQuaternaryWithIcon.jsx';
import { ButtonPrimaryPlain } from '../Form/Button/ButtonPrimaryPlain.jsx';
import { ButtonSecondaryPlain } from '../Form/Button/ButtonSecondaryPlain.jsx';

// graphql
import { fetchDJTsByStatusId } from '../graphqlCompnents/DJT/fetchDJTsByStatusId.jsx';
import { getStatusIdByStatusCode } from '../graphqlCompnents/DJT/getStatusIdByStatusCode.jsx';
import { fetchDailyJobTicketByIdForApproval } from '../graphqlCompnents/DJT/fetchDailyJobTicketByIdForApproval.jsx';
import { updateDJTStatus } from '../graphqlCompnents/DJT/updateDJTStatus.jsx';
import { updateDailyJobTicket } from '../graphqlCompnents/DJT/updateDailyJobTicket.jsx';

// icons
import { AvailableIcons } from '../Icons/AvailableIcons.jsx';

// structural
import { ModuleBox } from '../Structural/ModuleBox.jsx';
import { ModuleDrawer } from '../Structural/ModuleDrawer.jsx';

// components
import { ViewDailyJobTicket } from '../DailyJobTicket/ViewDailyJobTicket.jsx';

// form components
import { FormSelectSimple } from '../Form/Select/FormSelectSimple.jsx';
import { TextInput } from '../Form/Input/TextInput.jsx';
import { TextareaInput } from '../Form/Input/TextareaInput.jsx';
import { FormSwitch } from '../Form/Switch/FormSwitch.jsx';

// uuid
import { v4 as uuidv4 } from 'uuid';

// generic functions
import { todaysDateYMD, spellOutDate, netDateDueMutator } from '../functions/dateTime.jsx';
import { truncateString } from '../functions/strings.jsx';
import { convertCurrencyToDouble } from '../functions/currency.jsx';

// globals
import { AppContext } from '../AppContext.jsx';

// PDF
import { generatePdf } from '../PDF/GeneratePDF.jsx';
import { getIdentityId } from '../AWS/cognitoIdentityService.jsx';

// QuickBooks via proxy
import {
  getCustomerViaProxy,
  getTermsViaProxy,
  getItemsViaProxy,
  getClassesViaProxy,
  getCustomersViaProxy,
  // getAccessTokenViaProxy,
  // getRecentInvoicesViaProxy,
} from '../QuickBooks/qboClient.jsx';

import { useQboConnectionMeta } from "../QuickBooks/useQboConnectionMeta.jsx";

//  prop-types validation
import PropTypes from 'prop-types';

export const ViewApproveDJT = () => {
  // grab global context values
  const { store } = useContext(AppContext);
  // quickbooks environment, either sandbox or production
  const qbEnv = store?.qbEnvironment;

  const API_ENDPOINT = `${import.meta.env.VITE_API_BASE_URL}/qboproxy`;

  // Only allow usage when we *know* the state and it’s usable
  const { 
    meta, 
    loading, 
    error, 
    isConnected, 
    // isExpiringSoon,
    // state 
  } = useQboConnectionMeta(qbEnv, { enabled: Boolean(qbEnv) });

  // console.log('State: ', state);

  // Connection state checking
  const hasEnv = !!qbEnv;
  const hasMeta = !!meta;
  // Don’t treat “loading” as a fatal state; just don’t allow QBO actions yet
  const canUseQbo = isConnected;

  // For UI hints:
  const showInitBanner = hasEnv && loading && !hasMeta && !error;

  const [identityId, setIdentityId] = useState(null);

  useEffect(() => {
    const fetchIdentityId = async () => {
      try {
        const id = await getIdentityId();
        setIdentityId(id);
      } catch (error) {
        console.error("Error fetching identity ID:", error);
      }
    };

    fetchIdentityId();
  }, []);

  // ----- VIEW DJT DRAWER -----
  const { isOpen: isViewDjtOpen , onOpen: onViewDjtOpen, onClose: onViewDjtClose } = useDisclosure();

  // ----- MODAL -----
  const [ statusModalCloseDisabled, setStatsuModalCloseDisabled ] = useState(true);
  const [ statusProgress, setStatusProgress ] = useState(0);
  const [ statusModalContent, setStatusModalContent ] = useState('');
  const [ successMessage, setSuccessMessage ] = useState('');

  function updateStatusModal(progress, status) {
    setStatusProgress(progress);
    setStatusModalContent(status);
  }

  const { isOpen: isStatusModalOpen , onOpen: onStatusModalOpen, onClose: onStatusModalClose } = useDisclosure();
  
  function handleStatusModalClosed() {
    onStatusModalClose();
  }

  // ----- END MODAL -----

  const [ currentDJT, setCurrentDJT ] = useState(null);
  const [ lastQbInvoice, setLastQbInvoice ] = useState(null); 
  const [ classes, setClasses ] = useState(null);

  const [ attachFilesChecked, setAttachFilesChecked ] = useState(false);
  
  const [ sendToQBOnly, setSendToQBOnly ] = useState(false);

  const [ selectedCustomer, setSelectedCustomer ] = useState(null);
  const [ selectedClass, setSelectedClass ] = useState(null);
  const [ attachablesByDjt, setAttachablesByDjt ] = useState({});

  const [ invoiceDate, setInvoiceDate ] = useState(todaysDateYMD());
  const [ invoiceDueDate, setInvoiceDueDate ] = useState(todaysDateYMD());
  const [ terms, setTerms ] = useState([]);
  const [ selectedTerm, setSelectedTerm ] = useState('');
  const [ laborItemRefId, setLaborItemRefId ] = useState(null);
  const [ equipmentItemRefId, setEquipmentItemRefId ] = useState(null);
  const [ materialItemRefId, setMaterialItemRefId ] = useState(null);

  const CustomCloseDrawerButton = (type) => {
    let closeDrawer = null;
    if (type === 'view') {
      closeDrawer = onViewDjtClose;
    }

    return(
      <IconButton 
        variant={'icononlybutton'}
        icon={<AvailableIcons boxSize={'22px'} iconRef={'close'} />} 
        onClick={closeDrawer}
      />
    );
  };

  const [ pendingResponseStatusId, setPendingResponseStatusId ] = useState(null);
  const [ approvedStatusId, setApprovedStatusId ] = useState(null);
  const [ unapprovedStatusId, setUnapprovedStatusId ] = useState(null);
  const [ invoicedStatusId, setInvoicedStatusId ] = useState(null);
  const [ uninvoicedDoNotBillStatusId, setUninvoicedDoNotBillStatusId ] = useState(null);

  // character length of client and description text
  const clientTruncateValue = useBreakpointValue({
    sm: 10,
    md: 15,
    lg: 35,
    xl: 45,
    '2xl': 60
  });

  const descriptionTruncateValue = useBreakpointValue({
    sm: 10,
    md: 14,
    lg: 35,
    xl: 45,
    '2xl': 60
  });

  const searchHeaderTextClass = useBreakpointValue({
    sm: 'dark-sfpro-heading-2',
    md: 'dark-sfpro-heading-2',
    lg: 'dark-sfpro-heading-1',
    xl: 'dark-sfpro-heading-1',
    '2xl': 'dark-sfpro-heading-1'
  });

  useEffect(() => {
    const getStatusId = async () => {
      const statusId =  await getStatusIdByStatusCode('invoiced-via-quickbooks-native');
      return statusId;
    };
    getStatusId();
  }, []);

  useEffect(() => {
    const getStatusId = async () => {
      const statusId =  await getStatusIdByStatusCode('invoiced-via-quickbooks');
      return statusId;
    };
    getStatusId().then((id) => {
      setInvoicedStatusId(id);
    });
  }, []);

  useEffect(() => {
    const getStatusId = async () => {
      const statusId =  await getStatusIdByStatusCode('queued-pending-approval');
      return statusId;
    };
    getStatusId().then((id) => {
      setUnapprovedStatusId(id);
    });
  }, []);

  useEffect(() => {
    const getStatusId = async () => {
      const statusId =  await getStatusIdByStatusCode('uninvoiced-donotbill');
      return statusId;
    };
    getStatusId().then((id) => {
      setUninvoicedDoNotBillStatusId(id);
    });
  }, []);

  const [ djtId, setDjtId ] = useState(null);
  const handleOpenDrawer = (type, id) => {
    if (id) {
      if (type === 'view') {
        setDjtId(id, onViewDjtOpen());
      }
    }
  };

  const [ isRejectDJTVisible, setIsRejectDJTVisible ] = useState(false);
  const [ isInvoiceDJTVisible, setIsInvoiceDJTVisible ] = useState(false);

  const [ djtClientCompanyId, setDjtClientCompanyId ] = useState(null);
  const [ djtClientCompanyName, setDjtClientCompanyName ] = useState(null);
  const [ djtClientContactCompanyName, setDjtClientContactCompanyName ] = useState(null);
  const [ djtClientCompanyEmail, setDjtClientCompanyEmail ] = useState(null);
  const [ djtClientNeedsToSync, setDjtClientNeedsToSync ] = useState(false);
  const [ isFormLoaded, setIsFormLoaded ] = useState(false);
  const [ isMenuDisabled, setIsMenuDisabled ] = useState(false);
  const [ firstDjtLoaded, setFirstDjtLoaded ] = useState(null);

  const showApproval = async (id, index, environment, identityIdParam) => {
    onStatusModalOpen();
    updateStatusModal(10, 'Loading invoice');

    setIsFormLoaded(false);
    setIsMenuDisabled(true);
    if (!firstDjtLoaded) {
      setFirstDjtLoaded(unapprovedDJTs[index]);
    }
    await loadDJTForQB(unapprovedDJTs[index]);

    updateStatusModal(20, 'Fetching invoice data...');

    const qbContactCompanyName = unapprovedDJTs[index].djtClient?.contactCompany || '';
    setDjtClientContactCompanyName(qbContactCompanyName);
    const qbCompanyName = unapprovedDJTs[index].djtClient?.qbCompany || '';
    const qbCompanyEmail = unapprovedDJTs[index].djtClient?.qbEmail || '';
    setDjtClientCompanyName(qbCompanyName);
    setDjtClientCompanyEmail(qbCompanyEmail);
    let qbCompanyId = '';
    if (environment === 'sandbox') {
      qbCompanyId = unapprovedDJTs[index].djtClient?.qbSandboxId || '';
    } else {
      qbCompanyId = unapprovedDJTs[index].djtClient?.qbId || '';
    }
    if (qbCompanyName === '' || qbCompanyId === '' || qbCompanyEmail === '') {
      setDjtClientNeedsToSync(true);
    }
    setDjtClientCompanyId(qbCompanyId);

    updateStatusModal(30, 'Generating PDF for QuickBooks...');
    const pdfOk = await approveThisDJT(unapprovedDJTs[index], environment, identityIdParam);

    updateStatusModal(40, 'Loading QuickBooks customers...');

    // Previously: loadQBCustomers(tokenObj, environment).
    // We don't actually use the customer list in this component, but we can still
    // make a no-op call to confirm connectivity:
    if (environment) {
      try {
        await getCustomersViaProxy({ qbenv: environment, app: "shared" });
      } catch (e) {
        console.warn("getCustomersViaProxy failed (non-fatal for UI):", e);
      }
    }

    updateStatusModal(50, 'Loading QuickBooks classes...');
    
    await loadQBClasses(environment);

    updateStatusModal(90, 'Wrapping up...');
    setStatsuModalCloseDisabled(false);
    updateStatusModal(100, pdfOk ? 'Invoice loaded' : 'Invoice loaded without PDF');
    
    setCurrentDJT(unapprovedDJTs[index]);
    setIsRejectDJTVisible(false);
    setIsInvoiceDJTVisible(true);
    setDisableUnapprovedTable(false);
    setIsFormLoaded(true);
    setIsMenuDisabled(false);
    setSuccessMessage(pdfOk
      ? 'Your invoice has been loaded.'
      : 'The job ticket PDF could not be generated. The invoice can still be sent without the attachment.');
    };

  const [disabledToolbars, setDisabledToolbars] = useState({});
  const disableToolbar = (djtIdParam) => {
    setDisabledToolbars(prev => ({...prev, [djtIdParam]: true}));
  };
  const enableToolbar = (djtIdParam) => {
    setDisabledToolbars(prev => ({...prev, [djtIdParam]: false}));
  };

  const { control, register, handleSubmit, clearErrors, setValue, getValues, watch, formState: { errors } } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {}
  });

  const {
    fields: djtFields,
    append: djtAppend,
    remove: djtRemove,
  } = useFieldArray({ control, name: "djtrow" });

  const onError = (errs, e) => console.log(errs, e);

  const [ unapprovedDJTs, setUnapprovedDJTs ] = useState(null);
  const [ refreshUnapprovedDJTs, setRefreshUnapprovedDJTs ] = useState(null);
  const [ selectedClientId, setSelectedClientId ] = useState(null);
  const [ selectedPO, setSelectedPO ] = useState(null);

  const [ disableUnapprovedTable, setDisableUnapprovedTable ] = useState(false);
  
  useEffect(() => {
    function groupAndSort(arr) {
      const groupedByPo = arr.reduce((acc, obj) => {
        const key = obj?.djtPO?.po || 'No PO';
        if (!acc[key]) {
          acc[key] = [];
        }
        acc[key].push(obj);
        return acc;
      }, {});
    
      return Object.keys(groupedByPo).map(key => ({
        po: key,
        data: groupedByPo[key].sort((a, b) => new Date(a.djtDate) - new Date(b.djtDate)),
      }));
    }

    function flattenGroupedData(groupedData) {
      return groupedData.reduce((acc, group) => {
        const extendedData = group.data.map(item => ({
          ...item,
          po: group.po
        }));
        return acc.concat(extendedData);
      }, []);
    }

    function flattenAndFilterData(groupedData, clientId, po) {
      const flattenedData = groupedData.reduce((acc, group) => {
        const extendedData = group.data.map(item => ({
          ...item,
          po: group.po
        }));
        return acc.concat(extendedData);
      }, []);
    
      const filteredData = flattenedData.filter(item => {
        const matchesPo = po ? item.po === po : true;
        const matchesClientId = clientId ? item.djtClient.id === clientId : true;
        return matchesPo && matchesClientId;
      });
    
      return filteredData;
    }

    if (unapprovedStatusId && uninvoicedDoNotBillStatusId) {
      const filterByClientId = (array, clientId) => {
        return array.filter(item => item.djtClient.id === clientId);
      };
      
      const fetchDJTsInner = async () => {
        setIsFormLoaded(false);
        setIsMenuDisabled(true);
        setDisableUnapprovedTable(true);
        
        // Fetch DJTs from both statuses
        const unapprovedData = await fetchDJTsByStatusId(unapprovedStatusId);
        console.log('unapprovedData: ', unapprovedData);
        const uninvoicedData = await fetchDJTsByStatusId(uninvoicedDoNotBillStatusId);
        console.log('uninvoicedData: ', uninvoicedData);
        
        // Combine the data
        const combinedData = [...unapprovedData, ...uninvoicedData];

        if (selectedClientId) {
          const filteredArray = filterByClientId(combinedData, selectedClientId);
          const groupedAndSorted = groupAndSort(filteredArray);
          const flattenedFilteredData = flattenAndFilterData(groupedAndSorted, selectedClientId, selectedPO);
          setUnapprovedDJTs(flattenedFilteredData);
        } else {
          const groupedAndSorted = groupAndSort(combinedData);
          const flattenedGroupedData = flattenGroupedData(groupedAndSorted);
          setUnapprovedDJTs(flattenedGroupedData);
        }
        setIsFormLoaded(true);
        setIsMenuDisabled(false);
        setDisableUnapprovedTable(false);
      };
      fetchDJTsInner().catch(console.error);
    }
  },[unapprovedStatusId, uninvoicedDoNotBillStatusId, refreshUnapprovedDJTs, selectedClientId, selectedPO]);

  const fetchDJTs = useCallback(async () => {
    if (approvedStatusId) {
      await fetchDJTsByStatusId(approvedStatusId);
    }
  }, [approvedStatusId]);

  useEffect(() => {
    fetchDJTs().catch(console.error);
  }, [fetchDJTs]);

  const getApprovedId = useCallback(async () => {
    const data = await getStatusIdByStatusCode('queued-billing-approved');
    setApprovedStatusId(data);
  }, []);

  useEffect(() => {
    getApprovedId().catch(console.error);
  }, [getApprovedId]);

  const getPendingResponseId = useCallback(async () => {
    const data = await getStatusIdByStatusCode('queued-unapproved-with-issue');
    setPendingResponseStatusId(data);
  }, []);

  useEffect(() => {
    getPendingResponseId().catch(console.error);
  }, [getPendingResponseId]);

  const bounceDJT = async (ticket, reason) => {
    const djtIdLocal = ticket.id;
    const supervisorId = ticket?.teamMember?.id;
    const divisionId = ticket?.djtDivisionId;
    const clientId = ticket?.djtClient?.id;

    if (djtIdLocal && pendingResponseStatusId) {
      await updateDJTStatus(djtIdLocal, pendingResponseStatusId, divisionId, clientId, supervisorId);
    }

    const updateProps = {id: djtIdLocal, bouncedAdminReason: reason};
    const updateResult = await updateDailyJobTicket(updateProps);
    console.log('update result: ', updateResult);

    setRefreshUnapprovedDJTs(uuidv4());
    setSelectedClientId(null);
    setSelectedPO(null);
    setValue('message', '');
    setCurrentDJT(null);
    setIsRejectDJTVisible(false);
  };

  const generateInvoiceNumberWithTime = () => {
    const now = new Date();
    const datePrefix = now.toISOString().split('T')[0].replace(/-/g, '');
    const timeSuffix = now.toTimeString().split(' ')[0].replace(/:/g, '');
    return `ERP-${datePrefix}${timeSuffix}`;
  };

  const formSubmit = async (values, event) => {
    event.preventDefault();
    setIsFormLoaded(false);
    setIsMenuDisabled(true);

    const supervisorId = firstDjtLoaded?.teamMember?.id;
    const divisionId = firstDjtLoaded?.djtDivisionId;
    const clientId = firstDjtLoaded?.djtClient?.id;

    const attachFiles = attachFilesChecked || false;
    
    const djtCount = values?.djtrow?.length;
    const djtPO = values?.poNumber || '';
    const djtids = [];
    const invoiceNumber = generateInvoiceNumberWithTime();

    const params = {
      cnt:         djtCount,
      classname:   values?.selectClass?.label,
      classvalue:  values?.selectClass?.value,
      memo:        values?.customerMemo,
      sendto:      values?.customerEmail,
      invnum:      invoiceNumber,
      po:          djtPO,
      attachpdf:   attachFiles,
      sendtqbonly: sendToQBOnly,
    };

    values?.djtrow?.forEach((item, index) => {
      const att = attachablesByDjt[item?.djtId];
      if (att?.Id) {
        params[`${index}_fid`] = att.Id;
        params[`${index}_fn`]  = att.FileName;
      }
      params[`${index}_desc`]   = item?.djtNotes;
      params[`${index}_qbcid`]  = values?.customerId;
      params[`${index}_qbterm`] = values?.selectTerms?.value;
      params[`${index}_invd`]   = values?.invoiceDate;
      params[`${index}_invdd`]  = values?.invoiceDueDate;
      params[`${index}_subtot`] = convertCurrencyToDouble(item?.DJTSubtotal);
      params[`${index}_est`]    = convertCurrencyToDouble(item?.EquipmentTotal);
      params[`${index}_lst`]    = convertCurrencyToDouble(item?.LaborTotal);
      params[`${index}_mst`]    = convertCurrencyToDouble(item?.MaterialTotal);
      params[`${index}_sd`]     = item?.LaborServiceDate;
      params[`${index}_lir`]    = laborItemRefId;
      params[`${index}_eir`]    = equipmentItemRefId;
      params[`${index}_mir`]    = materialItemRefId;
      djtids.push(item?.djtId);
    });

    const qbResults = await createInvoice(params);
    console.info('qbResults: ', qbResults);

    if (qbResults.error) {
      alert(`Invoice creation failed: ${qbResults.error}`);
      setIsFormLoaded(true);
      setIsMenuDisabled(false);
      return;
    }

    // ✅ Update DJT status in Spike ERP
    for (const djt of djtids) {
      const djtIdLocal = djt;
      const updateResults = await updateDJTStatus(
        djtIdLocal,
        invoicedStatusId,
        divisionId,
        clientId,
        supervisorId,
        invoiceNumber
      );
      console.log('updateResults: ', updateResults);
    }

    // Capture QuickBooks invoice info (from Lambda: { invoiceId, ... })
    if (qbResults.invoiceId) {
      const link = `https://qbo.intuit.com/app/invoice?&txnId=${qbResults.invoiceId}`;
      setLastQbInvoice({
        id: qbResults.invoiceId,
        link,
        docNumber: invoiceNumber,
      });
    } else {
      setLastQbInvoice(null);
    }

    // Show success screen (hide invoice editor, re-enable form)
    setIsInvoiceDJTVisible(false);
    setIsFormLoaded(true);
    setIsMenuDisabled(false);
  };

  const [ djtGrandTotal, setDjtGrandTotal ] = useState(0);
  const [ customerDataLoaded, setCustomerDataLoaded ] = useState(false);

  const loadDJTForQB = async (data) => {
    setCustomerDataLoaded(false);
    const djtPONumber = data.djtPO?.po || null;
    const djtIdLocal = data.id;
    const clientIdLocal = data.djtClient?.id;
    setSelectedClientId(clientIdLocal);
    setSelectedPO(djtPONumber);

    const djt = await fetchDailyJobTicketByIdForApproval(djtIdLocal);
    console.warn('------> DJT: ', djt);

    const djtCustomerId = (qbEnv === 'sandbox')
      ? djt?.djtClient?.qbSandboxId
      : djt?.djtClient?.qbId;
    console.warn('djtCustomerId: ', djtCustomerId);

    const qbTerms = await getQBTerms();
    const qbItems = await getQBItems();
    console.warn(' ----- qbTerms: ', qbTerms);
    console.warn(' ----- qbItems: ', qbItems);

    const laborItem = qbItems?.find(item => item.Description === "Labor");
    setLaborItemRefId(laborItem?.Id);
    const equipmentItem = qbItems?.find(item => item.Description === "Equipment");
    setEquipmentItemRefId(equipmentItem?.Id);
    const materialItem = qbItems?.find(item => item.Description === "Material");
    setMaterialItemRefId(materialItem?.Id);

    const customerData = await getQBCustomer(djtCustomerId);
    console.warn(' ---- QB CUSTOMER ARRAY: ', customerData);

    const SalesTermRef = customerData?.SalesTermRef?.value || null;
    const condition = term => SalesTermRef ? term.Id === SalesTermRef : term.Term === '30';
    const selectedTermData = qbTerms.filter(condition);

    const selectedTermOption = {
      label: selectedTermData[0]?.Name, 
      value: selectedTermData[0]?.Id,
      term: selectedTermData[0]?.Term,
    };

    await handleTermsChanged(selectedTermOption);
    
    const qbCustomerId = (qbEnv === 'sandbox')
      ? djt.djtClient?.qbSandboxId
      : djt.djtClient?.qbId;
    setValue('customerId', qbCustomerId);
    setValue('customerName', customerData?.FullyQualifiedName || '');
    setValue('customerEmail', customerData?.PrimaryEmailAddr?.Address || '');
    
    const companyName = (customerData?.DisplayName || '') + '\n';
    const companyAddressLine1 = customerData?.BillAddr?.Line1 ? customerData.BillAddr.Line1 + '\n' : '';
    const companyCity = customerData?.BillAddr?.City || '';
    const companyState = customerData?.BillAddr?.CountrySubDivisionCode
      ? ', ' + customerData.BillAddr.CountrySubDivisionCode + '\n'
      : '';
    const companyPostalCode = customerData?.BillAddr?.PostalCode || '';

    const billingFormatted = companyName + companyAddressLine1 + companyCity + companyState + companyPostalCode;
    
    setValue('billingAddress', billingFormatted || '');
    setValue('poNumber', djtPONumber || '');

    const selected = {
      label: djt.djtClient?.qbDisplayName,
      value: qbCustomerId
    };
    setSelectedCustomer(selected);

    const djtSubtotal = (
      Number(djt.djtLaborTotal) +
      Number(djt.djtEquipmentTotal) +
      Number(djt.djtMaterialTotal)
    );

    const djtObject = {
      djtId: djtIdLocal,
      doNotBill: djt.doNotBill,
      doNotBillDescription: djt.doNotBillDescription,
      showLaborHours: djt.showLaborHours,
      specialInstructions: djt.specialInstructions,
      djtPoId: djtPONumber,
      djtNotes: djt?.djtNotes,
      AttachableId: djt?.attachableObjId,
      LaborServiceDate: djt.djtDate, 
      EquipmentServiceDate: djt.djtDate, 
      MaterialServiceDate: djt.djtDate, 
      LaborTotal: djt.djtLaborTotal, 
      LaborQuantity: '1', 
      LaborRate: djt.djtLaborTotal,
      LaborAmount: USDollar.format(djt.djtLaborTotal),
      EquipmentTotal: djt.djtEquipmentTotal, 
      EquipmentQuantity: '1', 
      EquipmentRate: djt.djtEquipmentTotal,
      EquipmentAmount: USDollar.format(djt.djtEquipmentTotal),
      MaterialTotal: djt.djtMaterialTotal, 
      MaterialQuantity: '1', 
      MaterialRate: djt.djtMaterialTotal,
      MaterialAmount: USDollar.format(djt.djtMaterialTotal),
      DJTSubtotal: USDollar.format(djtSubtotal)
    };
    djtAppend(djtObject);

    const totalAmount = parseFloat(djtGrandTotal) + parseFloat(djtSubtotal);
    setDjtGrandTotal(totalAmount);
    setValue('TotalAmount', USDollar.format(totalAmount) || '');

    setCustomerDataLoaded(true);
    return true;
  };

  const getQBCustomer = async (customerId) => {
    if (!qbEnv || !customerId) return null;
    try {
      const qbCustomer = await getCustomerViaProxy({
        qbenv: qbEnv,
        app: "shared",
        customerId,
      });
      return qbCustomer;
    } catch (e) {
      console.error("getQBCustomer failed:", e);
      return null;
    }
  };

  const loadQBClasses = async (environment) => {
    if (!environment) return;
    try {
      const qbClasses = await getClassesViaProxy({
        qbenv: environment,
        app: "shared",
      });
      setClasses(qbClasses || []);
    } catch (e) {
      console.error("getClassesViaProxy failed:", e);
      setClasses([]);
    }
  };

  const getQBItems = async () => {
    try {
      if (!qbEnv) throw new Error("No qbEnv available.");
      const qbItems = await getItemsViaProxy({
        qbenv: qbEnv,
        app: "shared",
      });

      if (!qbItems) {
        throw new Error('No items returned or bad response from getItemsViaProxy.');
      }

      if (!Array.isArray(qbItems)) {
        throw new Error('Expected items array from getItemsViaProxy.');
      }
  
      return qbItems;
    } catch (error) {
      console.error('Failed to fetch QB items:', error);
      return [];
    }
  };

  const getQBTerms = async () => {
    try {
      if (!qbEnv) {
        throw new Error("No qbEnv available.");
      }
      const qbTerms = await getTermsViaProxy({
        qbenv: qbEnv,
        app: "shared",
      });
  
      if (!qbTerms) {
        throw new Error("Failed to retrieve terms.");
      }
  
      const enhancedQbTerms = qbTerms.map(obj => {
        const match = obj.Name.match(/\d+/);
        const termValue = match ? match[0] : '0';
  
        return {
          ...obj,
          Term: termValue,
        };
      });
  
      setTerms(enhancedQbTerms);
      return enhancedQbTerms;
    } catch (error) {
      console.error("Error fetching QB Terms:", error);
      return [];
    }
  };

  const createInvoice = async (paramsObject) => {
    console.log(' ----- paramsObject: ', paramsObject);

    let response;
    let text;

    try {
      const session = await fetchAuthSession();
      const authToken = `Bearer ${session.tokens.idToken}`;

      response = await fetch(API_ENDPOINT,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: authToken,
          },
          body: JSON.stringify({
            operation: 'createQBInvoice',
            appEnv: import.meta.env.VITE_APP_ENV || 'production',
            qbenv: qbEnv,
            app: 'shared',
            payload: paramsObject,
          }),
        }
      );

      text = await response.text();
    } catch (e) {
      console.error('createInvoice: network error calling API Gateway:', e);
      return { error: 'Network error calling createQBInvoice' };
    }

    let dataJSON;
    try {
      dataJSON = JSON.parse(text);
    } catch (e) {
      console.error('createInvoice: non-JSON response:', text, 'status:', response?.status);
      return { error: `createQBInvoice responded with HTTP ${response?.status}` };
    }

    if (!response.ok) {
      console.error('createInvoice: error response:', dataJSON);
      // dataJSON here is the qboProxy wrapper — extract the inner body
      const inner = typeof dataJSON.body === 'string' ? JSON.parse(dataJSON.body) : dataJSON;
      return { error: inner?.error || text };
    }

    // Parse inner body from qboProxy wrapper before returning
    const inner = typeof dataJSON.body === 'string' ? JSON.parse(dataJSON.body) : dataJSON;
    return inner;
  };
 
  const approveThisDJT = async (djt, environment, identityIdParam) => {
    try {
      const result = await generatePdf(djt, null, environment, false, true, identityIdParam);
      const attachable = result?.attachable || result;
      if (attachable?.Id) {
        setAttachablesByDjt(prev => ({
          ...prev,
          [djt.id]: { Id: attachable.Id, FileName: attachable.FileName },
        }));
        return true;
      }
    } catch (e) {
      console.error("approveThisDJT: PDF generation failed", e);
    }
    return false;
  };

  const USDollar = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  });

  const [ term, setTerm ] = useState(0);
  const handleTermsChanged = async (option) => {
    const currentInvoiceDate = invoiceDate;
    const selectedTermValue = option?.term;
    setTerm(selectedTermValue);
    const newDueDate = netDateDueMutator(currentInvoiceDate, selectedTermValue, '-');
    setSelectedTerm(option);
    setValue('invoiceDueDate', newDueDate);
    setInvoiceDueDate(newDueDate);
    setValue('selectTerms', option);
    return;
  };

  const handleInvoiceDueDateChanged = (date) => {
    setInvoiceDueDate(date);
    setValue('invoiceDueDate', date);
  };

  const handleInvoiceDateChanged = (newInvoiceDate) => {
    const currentTerm = Number(term);
    const newDueDate = netDateDueMutator(newInvoiceDate, currentTerm, '-');
    setValue('invoiceDueDate', newDueDate);
    setInvoiceDueDate(newDueDate);
    setInvoiceDate(newInvoiceDate);
    setValue('invoiceDate', newInvoiceDate);
  };

  const [ isLoadingComplete, setIsLoadingComplete ] = useState(false);
  useEffect(() => {
    if (
      selectedTerm !== '' &&
      customerDataLoaded === true
    ) {
      setIsLoadingComplete(true);
    }
  },[ 
    selectedTerm, 
    customerDataLoaded 
  ]);

  const handleInvoiceReset = async () => {
    setDjtGrandTotal(0);
    setValue('TotalAmount', 0);
    setValue('customerMemo', 'Thank you for your business.');
    setValue('attachFiles', false);
    setAttachFilesChecked(false);
    setAttachablesByDjt({});
    setRefreshUnapprovedDJTs(uuidv4());
    setCurrentDJT(null);
    setIsInvoiceDJTVisible(false);
    setSelectedClientId(null);
    setSelectedPO(null);
    setSelectedClass(null);
    setValue('selectClass', '');
    setSelectedClassName(null);
    setDisabledToolbars({});
    djtRemove();
    setFirstDjtLoaded(null);
    setIsFormLoaded(true);
    setIsMenuDisabled(false);
    setLastQbInvoice(null);
    return;
  };

  const [ selectedClassName, setSelectedClassName ] = useState(null);
  const handleClassChanged = (option) => {
    setSelectedClass(option.value);
    setSelectedClassName(option.label);
    setValue('selectClass', option);
    clearErrors(['selectClass']);
  };

  const ObjectRenderer = ({ obj }) => {
    if (!obj) {
      return null;
    }
  
    const renderValue = (value) => {
      if (typeof value === 'object' && value !== null && !(value instanceof Array)) {
        return <div style={{marginLeft: '20px'}}><ObjectRenderer obj={value} /></div>;
      } else if (value instanceof Array) {
        return value.map((item, index) => (
          <div key={index} style={{marginLeft: '20px'}}>
            {renderValue(item)}
          </div>
        ));
      }
      return value.toString();
    };
  
    return (
      <div>
        {Object.entries(obj).map(([key, value]) => (
          <div key={key}>
            <span>{`${key}: `}</span>
            {value !== null && renderValue(value)}
          </div>
        ))}
      </div>
    );
  };
  
  ObjectRenderer.propTypes = {
    obj: PropTypes.object.isRequired
  };

  // eslint-disable-next-line no-unused-vars
  const [ matchedQBCustomer, setMatchedQBCustomer ] = useState(null);
  useEffect(() => {
    if (matchedQBCustomer) {
      // placeholder to find closest match if needed later
    }
  },[matchedQBCustomer]);

  // eslint-disable-next-line no-unused-vars
  const [ isRejectDJTVisibleState, setIsRejectDJTVisibleState ] = useState(false);
  useEffect(() => {
    setIsRejectDJTVisible(isRejectDJTVisibleState);
  }, [isRejectDJTVisibleState]);

  // eslint-disable-next-line no-unused-vars
  const [ isMenuDisabledState, setIsMenuDisabledState ] = useState(false);
  useEffect(() => {
    setIsMenuDisabled(isMenuDisabledState);
  }, [isMenuDisabledState]);

  // eslint-disable-next-line no-unused-vars
  const [ isRejectVisible, setIsRejectVisible ] = useState(false);
  useEffect(() => {
    setIsRejectDJTVisible(isRejectVisible);
  }, [isRejectVisible]);

  useEffect(() => {
    const shouldDisableMenu = djtFields.some(item => item?.doNotBill);
    setIsMenuDisabled(shouldDisableMenu);
  }, [djtFields]);

  const missingAttachments =
    attachFilesChecked && djtFields.some(f => !attachablesByDjt[f.djtId]?.Id);

  const isSubmitDisabled =
    !isFormLoaded || djtFields?.length === 0 || !selectedClass || missingAttachments;

  console.warn("DJT submit gate:", {
    isFormLoaded,
    djtFieldsLength: djtFields?.length,
    attachFilesChecked,
    attachablesCount: Object.keys(attachablesByDjt).length,
    missingAttachments,
    isSubmitDisabled,
  });

  let isDebug = false;

  if (!hasEnv) {
    return (
      <Container maxW="1600" mb={12}>
        <ModuleBox>
          <Text>
            QuickBooks environment is not configured. Set the QuickBooks
            environment in the Admin dashboard.
          </Text>
        </ModuleBox>
      </Container>
    );
  }

  if (!canUseQbo && !loading && !error && hasEnv) {
    return (
      <Container maxW="1600" mb={12}>
        <ModuleBox>
          <Text>
            QuickBooks shared connection is not active. An admin may need to
            re-authorize QuickBooks from the Admin dashboard.
          </Text>
        </ModuleBox>
      </Container>
    );
  }

  return(
    <>
      <Container as="form" maxW="1600" mb={12}>
        <>
          {showInitBanner && (
            <ModuleBox mb={4}>
              <Text>
                Checking QuickBooks shared connection…
              </Text>
            </ModuleBox>
          )}

          {/* Top table (unapproved DJTs) */}
          <fieldset disabled={disableUnapprovedTable}>
            <ModuleBox>
              <TableContainer>
                <HStack paddingBottom={'10px'}>
                  <Box w={'100%'}>
                    <Text className={searchHeaderTextClass}>
                      {(isInvoiceDJTVisible === true)
                        ? 'Queue for QuickBooks'
                        : 'View & Invoice Daily Job Tickets'}
                    </Text>
                  </Box>
                  <Box>
                    <ButtonQuaternaryWithIcon
                      name='refresh'
                      leftIcon='refresh'
                      iconsize='22px'
                      isDisabled={isMenuDisabled}
                      onClick={() => { handleInvoiceReset(); }}
                      value='Refresh page'
                    />
                  </Box>
                </HStack>
                                    
                <Table variant={'compact'} size={'compact'}>
                  <Thead>
                    <Tr>
                      <Th><Text as="span" textStyle='dark-heading-4'>CLIENT</Text></Th>
                      <Th><Text as="span" textStyle='dark-heading-4'>DATE</Text></Th>
                      <Th><Text as="span" textStyle='dark-heading-4'>PO</Text></Th>
                      <Th><Text as="span" textStyle='dark-heading-4'>DESCRIPTION</Text></Th>
                      <Th textAlign='left' pl={'38px'}>
                        <Text as="span" textStyle='dark-heading-4'>ACTIONS</Text>
                      </Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {(unapprovedDJTs?.length === 0) &&
                      <Tr>
                        <Td py='6px' colSpan={4}>
                          <Center>No results found</Center>
                        </Td>
                      </Tr>
                    }
                    {unapprovedDJTs?.map((djt, index) => {
                      const goesBy = djt?.teamMember?.goesBy;
                      const firstName = (goesBy && goesBy !== "")
                        ? goesBy
                        : djt?.teamMember?.firstName;
                      const supervisorName = `${firstName} ${djt?.teamMember?.lastName}`;
                      return(
                        <Fragment key={'frag_1_' + index}>
                          <Tr key={'tr_' + index}>
                            <Td py='6px' key={'td_2_' + index}>
                              <HStack>
                                {(disabledToolbars[djt?.id]) && (
                                  <Box paddingTop={'2px'}>
                                    <AvailableIcons boxSize={'15px'} iconRef={'outlinecheckcircle'} />
                                  </Box>
                                )}
                                <Box>
                                  <Text paddingLeft={'3px'} as="span" className='dark-sfpro-text-2'>
                                    <Tooltip label={djt?.djtClient?.contactCompany}>
                                      <span>
                                        {truncateString(
                                          djt?.djtClient?.contactCompany,
                                          clientTruncateValue
                                        )}
                                      </span>
                                    </Tooltip>
                                  </Text>
                                </Box>
                              </HStack>
                            </Td>
                            <Td py='6px' key={'td_1_' + index}>
                              <Text as="span" className='dark-sfpro-text-2'>
                                {spellOutDate(djt?.djtDate, 'apple-fy')}
                              </Text>
                            </Td>
                            <Td py='6px' key={'td_6_' + index}>
                              <Text as="span" className='dark-sfpro-text-2'>
                                {djt?.djtPO?.po}
                              </Text>
                            </Td>
                            <Td py='6px' key={'td_3_' + index} my='5px'>
                              <Text as="span" className='dark-sfpro-text-2' my='5px'>
                                <Tooltip
                                  label={
                                    <Stack m='10px'>
                                      <Text>{`Supervisor: ${supervisorName}`}</Text>
                                      <Text>{`${djt?.djtNotes}`}</Text>
                                    </Stack>
                                  }
                                >
                                  <span>{truncateString(djt?.djtNotes, descriptionTruncateValue)}</span>
                                </Tooltip>
                              </Text>
                            </Td>
                            <Td key={'td_4_' + index}>
                              <Box w='100%' align='right'>
                                <Menu>
                                  <MenuButton
                                    as={IconButton}
                                    aria-label='Options'
                                    icon={<AvailableIcons boxSize={'22px'} iconRef={'more'} />} 
                                    variant='withIconOnlySenary' 
                                    backgroundColor={'var(--dark-main-background)'}
                                    w={'60px'}
                                    iconsize='18px'
                                    height='28px'
                                    mb='3px'
                                    isDisabled={
                                      !isFormLoaded ||
                                      isMenuDisabled ||
                                      disabledToolbars[djt?.id] ||
                                      !qbEnv ||
                                      !identityId
                                    }
                                  />
                                  <MenuList>
                                    <MenuItem 
                                      onClick={() => handleOpenDrawer('view', djt?.id)}
                                      icon={<AvailableIcons boxSize={'22px'} iconRef={'view'} />} >
                                      View Job Ticket
                                    </MenuItem>

                                    <MenuItem 
                                      onClick={() => {
                                        setDisableUnapprovedTable(true);
                                        setCurrentDJT(unapprovedDJTs[index]);
                                        setIsRejectDJTVisible(true);
                                        setIsInvoiceDJTVisible(false);
                                        disableToolbar(djt?.id);
                                      }}
                                      icon={<AvailableIcons boxSize={'22px'} iconRef={'reject'} />} >
                                      Return to Supervisor
                                    </MenuItem>

                                    <MenuItem 
                                      isDisabled={
                                        !canUseQbo || 
                                        disabledToolbars[djt?.id] ||
                                        !qbEnv ||
                                        !identityId
                                      }
                                      onClick={() => {
                                        if (!canUseQbo) {
                                          console.warn(
                                            "Queue for QuickBooks blocked: QuickBooks shared connection is not usable."
                                          );
                                          return;
                                        }

                                        setIsMenuDisabled(true);
                                        setDisableUnapprovedTable(true);
                                        showApproval(djt?.id, index, qbEnv, identityId);
                                        disableToolbar(djt?.id);
                                      }}
                                      icon={<AvailableIcons boxSize={'22px'} iconRef={'upload'} />} >
                                      Queue for QuickBooks
                                    </MenuItem>
                                  </MenuList>
                                </Menu>
                              </Box>
                            </Td>
                          </Tr>
                          <Tr key={'tr_divider_b_' + index}>
                            <Td
                              key={'td_divider_b_' + index}
                              colSpan={5}
                              borderBottom={'1px solid var(--dark-module-divider)'}
                            />
                          </Tr>
                        </Fragment>
                      );
                    })}
                    <Tr>
                      <Td colSpan={5} borderBottom={'1px solid var(--dark-module-divider)'} />
                    </Tr> 
                  </Tbody>
                </Table>
              </TableContainer>
            </ModuleBox>
          </fieldset>

          {/* Reject form — kept OUTSIDE the table fieldset above.
              The "Return to Supervisor" action sets disableUnapprovedTable=true
              to lock the table; a native <fieldset disabled> also disables every
              nested control, which previously killed this form's inputs/buttons. */}
          {(isRejectDJTVisible) && (
              <ModuleBox>
                <Box>
                  <Text>Date: {currentDJT?.djtDate}</Text>
                  <Text>
                    Company: {currentDJT?.djtClient?.contactCompany || currentDJT?.djtClient?.qbCompany}
                  </Text>
                  <Text>Job Description: {currentDJT?.djtNotes}</Text>
                  <Text>Labor Total Amount: {USDollar.format(currentDJT?.djtLaborTotal || null)}</Text>
                  <Text>Equipment Total Amount: {USDollar.format(currentDJT?.djtEquipmentTotal || null)}</Text>
                  <Text>Material Total Amount: {USDollar.format(currentDJT?.djtMaterialTotal || null)}</Text>
                  <Text>Total Amount: {USDollar.format(currentDJT?.djtTotalBilled || null)}</Text>
                  <Text mb='20px'>
                    Supervisor: {currentDJT?.teamMember?.goesBy} {currentDJT?.teamMember?.firstName}{' '}
                    {currentDJT?.teamMember?.lastName}
                  </Text>
                  
                  <TextInput
                    fieldname="message"
                    fieldlabel="Approval Rejection Reason"
                    prettyname="Message"
                    register={register}
                    errors={errors}
                    placeholder="Reason for approval rejection"
                    isRequired={true} 
                    isReadOnly={false}
                  />

                  <TextInput
                    fieldname='djtId'
                    fieldvalue={currentDJT?.id || ''}
                    register={register}
                    errors={errors}
                    fieldtype='hidden'
                  />
                  <TextInput
                    fieldname='supervisorId'
                    fieldvalue={currentDJT?.teamMember?.id || ''}
                    register={register}
                    errors={errors}
                    fieldtype='hidden'
                  />
                  <TextInput
                    fieldname='djtDate'
                    fieldvalue={currentDJT.djtDate || ''}
                    register={register}
                    errors={errors}
                    fieldtype='hidden'
                  />
                  <TextInput
                    fieldname='divisionId'
                    fieldvalue={currentDJT.djtDivisionId || ''}
                    register={register}
                    errors={errors}
                    fieldtype='hidden'
                  />
                  <TextInput
                    fieldname='company'
                    fieldvalue={
                      currentDJT?.djtClient?.contactCompany ||
                      currentDJT?.djtClient?.qbCompany ||
                      ''
                    }
                    register={register}
                    errors={errors}
                    fieldtype='hidden'
                  />
                  <TextInput
                    fieldname='clientId'
                    fieldvalue={currentDJT?.djtClient?.id || ''}
                    register={register}
                    errors={errors}
                    fieldtype='hidden'
                  />
                  <TextInput
                    fieldname='totalAmount'
                    fieldvalue={USDollar.format(currentDJT?.djtTotalBilled || '')}
                    register={register}
                    errors={errors}
                    fieldtype='hidden'
                  />

                  <HStack my={'25px'} key={uuidv4()}>
                    <Box w={'25%'}>
                      <ButtonSecondaryPlain 
                        width={'100%'}
                        name='cancel'
                        value='Cancel'
                        onClick={() =>  handleInvoiceReset()}
                        isDisabled={!isFormLoaded}
                      />
                    </Box>

                    <Box w={'75%'}>
                      <ButtonPrimaryPlain 
                        width={'100%'}
                        name='reject'
                        value='Reject & Return to Supervisor'
                        onClick={() =>  bounceDJT(currentDJT, getValues('message').trim())}
                        isDisabled={!isRejectDJTVisible || !watch('message')?.trim()}
                      />
                    </Box>
                  </HStack>
                </Box>
              </ModuleBox>
          )}

          {/* Invoice section */}
          <fieldset disabled={!isLoadingComplete}>
            {(isInvoiceDJTVisible) && (
              <>
                {(isDebug) && (
                  <ModuleBox>
                    <Box>
                      <Text as="span" textStyle='dark-heading-1'>DEBUG ONLY, WILL BE REMOVED</Text>
                    </Box>
                    <Box>
                      <Text as="span" textStyle='dark-heading-2'>Environment: {qbEnv}</Text>
                    </Box>
                    <Box>
                      <Text as="span" textStyle='dark-heading-2'>
                        djtClientCompanyName: {djtClientCompanyName}
                      </Text>
                    </Box>
                    <Box>
                      <Text as="span" textStyle='dark-heading-2'>
                        djtClientCompanyId: {djtClientCompanyId}
                      </Text>
                    </Box>
                    <Box>
                      <Text as="span" textStyle='dark-heading-2'>
                        djtClientCompanyEmail: {djtClientCompanyEmail}
                      </Text>
                    </Box>
                    <Box>
                      <Text as="span" textStyle='dark-heading-2'>
                        djtClientNeedsToSync: {djtClientNeedsToSync?.toString()}
                      </Text>
                    </Box>
                    <Box>
                      <Text as="span" textStyle='dark-heading-2'>
                        djtClientComtactCompanyName: {djtClientContactCompanyName}
                      </Text>
                    </Box>
                    <Box>
                      <Text as="span" textStyle='dark-heading-2'>
                        djtClient: {JSON.stringify(currentDJT?.djtClient)}
                      </Text>
                    </Box>
                    <Box>
                      <Text as="span" textStyle='dark-heading-2'>
                        QB Selected Client: {JSON.stringify(selectedCustomer)}
                      </Text>
                    </Box>
                  </ModuleBox>
                )}

                <ModuleBox>
                  <HStack>
                    <Box w='50%'>
                      <TextInput
                        fieldname="customerName"
                        fieldlabel="QB Customer Name"
                        prettyname="QB Customer Name"
                        register={register}
                        errors={errors}
                        placeholder="QB Customer Name"
                        isRequired={true} 
                        isReadOnly={true}
                      />
                    </Box>
                    <Box w='50%'>
                      <TextInput
                        fieldname="customerEmail"
                        fieldlabel="QB Customer Email"
                        prettyname="QB Customer Email"
                        register={register}
                        errors={errors}
                        placeholder="QB Customer Email"
                        isRequired={true} 
                        isReadOnly={true}
                      />
                    </Box>
                  </HStack>

                  <Flex my={'25px'}>
                    <Box w={'30%'} mr={'10px'}>
                      <TextareaInput
                        fieldname="billingAddress"
                        fieldlabel="Billing Address"
                        prettyname="Billing Address"
                        register={register}
                        errors={errors}
                        placeholder="Billing Address"
                        isRequired={true}
                        isReadOnly={true}
                        rows={5}
                      />
                    </Box>
                    <Spacer/>
                    <Box w={'15%'} mr={'10px'}>
                      {(terms && terms !== null && terms !== undefined) && 
                        <FormSelectSimple
                          register={register}
                          control={control}
                          errors={errors}
                          isRequired={true}
                          rules={{ required: "Terms is required" }}
                          onChange={e => handleTermsChanged(e)}
                          selectedoption={selectedTerm || ''}
                          optionsArray={
                            terms?.map((options) => ({ 
                              label: options?.Name,
                              value: options?.Id,
                              term: options?.Term
                            }))
                          }
                          fieldname='selectTerms'
                          prettyname='Terms'
                          fieldlabel="Terms"
                          placeholder={'Select Terms'}
                        />}
                    </Box>
                    <Spacer/>
                    <Box w={'15%'} mr={'10px'}>
                      <TextInput
                        register={register}
                        errors={errors}
                        fieldname="invoiceDate"
                        fieldlabel="Invoice Date"
                        fieldvalue={invoiceDate || ''}
                        prettyname="Invoice Date"
                        onChange={event => handleInvoiceDateChanged(event.target.value)}
                        isRequired={true} 
                        fieldtype='date'
                      />
                    </Box>
                    <Spacer/>
                    <Box w={'15%'} mr={'10px'}>
                      <TextInput
                        register={register}
                        errors={errors}
                        fieldname="invoiceDueDate"
                        fieldlabel="Due Date"
                        fieldvalue={invoiceDueDate || ''}
                        prettyname="Due Date"
                        onChange={event => handleInvoiceDueDateChanged(event.target.value)}
                        isRequired={true} 
                        fieldtype='date'
                      />
                    </Box>
                    <Spacer/>
                    <Box w={'15%'}>
                      <TextInput
                        fieldname="poNumber"
                        fieldlabel="P.O. Number"
                        prettyname="P.O. Number"
                        register={register}
                        errors={errors}
                        placeholder="P.O. Number"
                        isRequired={false}
                      />
                    </Box>
                  </Flex>

                  <Box w='30%'>
                    <FormSelectSimple
                      key='select_class'
                      register={register}
                      control={control}
                      errors={errors}
                      isRequired={true}
                      rules={{ required: "Class is required" }}
                      onChange={(e) => handleClassChanged(e)}
                      selectedOption={selectedClass || ''}
                      optionsArray={classes?.map(({ Id, FullyQualifiedName }) => ({
                        label: `${FullyQualifiedName}`,
                        value: Id,
                      }))}
                      fieldname="selectClass"
                      fieldlabel='QuickBooks Class'
                      prettyname='Class'
                      placeholder={'Select class or start typing'}
                    />                        
                  </Box>

                  <Flex my={'25px'}>
                    <Box>
                      <Text as="span" textStyle='heading-1'>Attach Files</Text><br/>
                      <Text as="span" color={'var(--dark-text-grey-2)'} textStyle='text-2'>
                        Choose to attach the Daily Job Tickets to the emailed invoice.
                      </Text>
                    </Box>
                    <Spacer />
                    <Box>
                      <FormSwitch
                        register={register}
                        control={control}
                        fieldname="attachFiles"
                        isChecked={attachFilesChecked}
                        onChange={() => {
                          setValue('attachFiles', !attachFilesChecked);
                          setAttachFilesChecked(!attachFilesChecked);
                        }}
                      />
                    </Box>
                  </Flex>

                  <Flex my={'25px'}>
                    <Box>
                      <Text as="span" textStyle='heading-1'>Submit to QB Only</Text><br/>
                      <Text as="span" color={'var(--dark-text-grey-2)'} textStyle='text-2'>
                        Invoices will post to QuickBooks, but will not be emailed to clients.
                      </Text>
                    </Box>
                    <Spacer />
                    <Box>
                      <FormSwitch
                        register={register}
                        control={control}
                        fieldname="sendEmail"
                        isChecked={sendToQBOnly || false}
                        onChange={() => {
                          setValue('sendToQBOnly', !sendToQBOnly);
                          setSendToQBOnly(!sendToQBOnly);
                        }}
                      />
                    </Box>
                  </Flex>
                </ModuleBox>

                <ModuleBox>                    
                  <Box mt={5}>
                    {djtFields && djtFields.map((item, index) => {
                      const doNotBill = item?.doNotBill || false;
                      const doNotBillDescription = item?.doNotBillDescription || '';
                      const specialInstructions = item?.specialInstructions || null;

                      return (
                        <React.Fragment key={uuidv4()}>
                          {(index > 0) && (
                            <Divider marginTop={'25px'} marginBottom={'15px'} />
                          )}
                          <HStack width={'full'} paddingBottom={'25px'}>
                            <Box>
                              <Text as="span" textStyle='dark-text-1'>
                                {spellOutDate(item?.LaborServiceDate, 'apple-fy')}
                              </Text>
                              <Text
                                as="span"
                                color='var(--error-red)'
                                textStyle='dark-text-1'
                              >
                                {doNotBill && ' - DO NOT INVOICE: ' + doNotBillDescription}
                              </Text>
                            </Box>
                            <Spacer />
                            <Box>
                              <ButtonSenaryWithIconOnly
                                key={index + '_button_' + item.id}
                                name='remove'
                                icon='delete'
                                iconsize='22px'
                                onClick={() => {
                                  let subTotal = getValues(`djtrow[${index}].DJTSubtotal`);
                                  let totalAmount = getValues('TotalAmount');
                                  
                                  subTotal = convertCurrencyToDouble(subTotal);                                    
                                  totalAmount = convertCurrencyToDouble(totalAmount);

                                  const grandTotal = Number(totalAmount) - Number(subTotal);
                                  setDjtGrandTotal(grandTotal);

                                  const newTotalAmount = Number(totalAmount) - Number(subTotal);
                                  setValue('TotalAmount', USDollar.format(newTotalAmount));
                                  
                                  djtRemove(index);
                                  setAttachablesByDjt(prev => {
                                    const next = { ...prev };
                                    delete next[item?.djtId];
                                    return next;
                                  });
                                  enableToolbar(item?.djtId);
                                  setIsMenuDisabled(false);
                                }}
                              />
                            </Box>
                          </HStack>

                          {specialInstructions && (
                            <Box paddingBottom={'25px'}>
                              <Text as="span" textStyle='dark-text-1'>
                                SPECIAL BILLING INSTRUCTIONS:{" "}
                              </Text>
                              <Text as="span" textStyle='dark-text-1'>
                                {specialInstructions}
                              </Text>
                            </Box>
                          )}
                          
                          {/* Labor row */}
                          <HStack spacing={2} key={uuidv4()}>
                            <Box w='15%' key={uuidv4()}>
                              <TextInput
                                key={index + '_1_1_' + item.id}
                                fieldname={`djtrow[${index}].LaborServiceDate`}
                                placeholder="Service Date"
                                register={register}
                                errors={errors}
                                isRequired={true} 
                                isReadOnly={true}
                                fieldlabel="Service Date"
                              />
                            </Box>

                            <Box w='15%' key={uuidv4()}>
                              <TextInput
                                key={index + '_1_2_' + item.id}
                                fieldname={`djtrow[${index}].LaborDescription`}
                                fieldlabel="Product/Service"
                                fieldvalue={'Labor'}
                                register={register}
                                errors={errors}
                                isRequired={true} 
                                isReadOnly={true}
                              />
                            </Box>

                            <Box key={uuidv4()}>
                              <TextInput
                                key={index + '_1_3_' + item.id}
                                fieldname={`djtrow[${index}].LaborNotes`}
                                fieldlabel="Description"
                                prettyname="Description"
                                fieldvalue={item?.djtNotes || ''}
                                register={register}
                                errors={errors}
                                isReadOnly={true}
                              />
                            </Box>

                            <Box w='15%' key={uuidv4()}>
                              <TextInput
                                key={index + '_1_4_' + item.id}
                                fieldname={`djtrow[${index}].LaborClass`}
                                fieldlabel="Class (hidden)"
                                fieldvalue={selectedClassName || ''}
                                register={register}
                                errors={errors}
                                isReadOnly={true}
                                placeholder={selectedClassName || ''}
                              />
                            </Box>

                            <Box w='15%' key={uuidv4()}>
                              <TextInput
                                key={index + '_1_5_' + item.id}
                                fieldname={`djtrow[${index}].LaborAmount`}
                                fieldlabel="Amount"
                                textAlign='right'
                                register={register}
                                errors={errors}
                                isRequired={true} 
                                isReadOnly={true}
                              />
                            </Box>
                          </HStack>
                          
                          <TextInput
                            key={index + '_1_6_' + item.id}
                            fieldname={`djtrow[${index}].LaborQuantity`}
                            register={register}
                            errors={errors}
                            isRequired={true} 
                            isReadOnly={true}
                            fieldtype='hidden'
                          />

                          <TextInput
                            key={index + '_1_7_' + item.id}
                            fieldname={`djtrow[${index}].LaborRate`}
                            register={register}
                            errors={errors}
                            fieldtype='hidden'
                          />

                          {/* Equipment row */}
                          <HStack spacing={2} key={uuidv4()}>
                            <Box w='15%' key={uuidv4()}>
                              <TextInput
                                key={index + '_2_1_' + item.id}
                                fieldname={`djtrow[${index}].EquipmentServiceDate`}
                                placeholder="Service Date"
                                register={register}
                                errors={errors}
                                isRequired={true} 
                                isReadOnly={true}
                              />
                            </Box>

                            <Box w='15%' key={uuidv4()}>
                              <TextInput
                                key={index + '_2_2_' + item.id}
                                fieldname={`djtrow[${index}].EquipmentDescription`}
                                fieldvalue={'Equipment'}
                                register={register}
                                errors={errors}
                                isRequired={true} 
                                isReadOnly={true}
                              />
                            </Box>

                            <Box key={uuidv4()}>
                              <TextInput
                                key={index + '_2_3_' + item.id}
                                fieldname={`djtrow[${index}].EquipmentNotes`}
                                fieldvalue={''}
                                register={register}
                                errors={errors}
                                isReadOnly={true}
                                placeholder={''}
                              />
                            </Box>

                            <Box w='15%' key={uuidv4()}>
                              <TextInput
                                key={index + '_2_4_' + item.id}
                                fieldname={`djtrow[${index}].EquipmentClass`}
                                fieldvalue={selectedClassName || ''}
                                register={register}
                                errors={errors}
                                isReadOnly={true}
                                placeholder={selectedClassName || ''}
                              />
                            </Box>

                            <Box w='15%' key={uuidv4()}>
                              <TextInput
                                key={index + '_2_5_' + item.id}
                                fieldname={`djtrow[${index}].EquipmentAmount`}
                                register={register}
                                textAlign='right'
                                errors={errors}
                                isRequired={true} 
                                isReadOnly={true}
                              />
                            </Box>
                          </HStack>

                          <TextInput
                            key={index + '_2_6_' + item.id}
                            fieldname={`djtrow[${index}].EquipmentQuantity`}
                            register={register}
                            errors={errors}
                            isRequired={true} 
                            isReadOnly={true}
                            fieldtype='hidden'
                          />

                          <TextInput
                            key={index + '_2_7_' + item.id}
                            fieldname={`djtrow[${index}].EquipmentRate`}
                            register={register}
                            errors={errors}
                            fieldtype='hidden'
                          />

                          {/* Material row */}
                          <HStack spacing={2} key={uuidv4()}>
                            <Box w='15%' key={uuidv4()}>
                              <TextInput
                                key={index + '_3_1_' + item.id}
                                fieldname={`djtrow[${index}].MaterialServiceDate`}
                                placeholder="Service Date"
                                register={register}
                                errors={errors}
                                isRequired={true} 
                                isReadOnly={true}
                              />
                            </Box>

                            <Box w='15%' key={uuidv4()}>
                              <TextInput
                                key={index + '_3_2_' + item.id}
                                fieldname={`djtrow[${index}].MaterialDescription`}
                                fieldvalue={'Material'}
                                register={register}
                                errors={errors}
                                isRequired={true} 
                                isReadOnly={true}
                              />
                            </Box>

                            <Box key={uuidv4()}>
                              <TextInput
                                key={index + '_3_3_' + item.id}
                                fieldname={`djtrow[${index}].MaterialNotes`}
                                fieldvalue={''}
                                register={register}
                                errors={errors}
                                isReadOnly={true}
                                placeholder={''}
                              />
                            </Box>

                            <Box w='15%' key={uuidv4()}>
                              <TextInput
                                key={index + '_3_4_' + item.id}
                                fieldname={`djtrow[${index}].MaterialClass`}
                                fieldvalue={selectedClassName || ''}
                                register={register}
                                errors={errors}
                                isReadOnly={true}
                                placeholder={selectedClassName || ''}
                              />
                            </Box>

                            <Box w='15%' key={uuidv4()}>
                              <TextInput
                                key={index + '_3_5_' + item.id}
                                fieldname={`djtrow[${index}].MaterialAmount`}
                                register={register}
                                textAlign='right'
                                errors={errors}
                                isRequired={true} 
                                isReadOnly={true}
                              />
                            </Box>
                          </HStack>

                          <TextInput
                            key={index + '_3_6_' + item.id}
                            fieldname={`djtrow[${index}].MaterialQuantity`}
                            register={register}
                            errors={errors}
                            isRequired={true} 
                            isReadOnly={true}
                            fieldtype='hidden'
                          />

                          <TextInput
                            key={index + '_3_7_' + item.id}
                            fieldname={`djtrow[${index}].MaterialRate`}
                            register={register}
                            errors={errors}
                            fieldtype='hidden'
                          />

                          <Flex key={uuidv4()}>
                            <Box key={uuidv4()} />
                            <Spacer />
                            <Box w={'25%'} key={uuidv4()} mt={5}>
                              <TextInput
                                key={index + '_6_' + item.id}
                                fieldname={`djtrow[${index}].DJTSubtotal`}
                                fieldlabel="Subtotal"
                                textAlign='right'
                                register={register}
                                errors={errors}
                                isRequired={true} 
                                isReadOnly={true}
                              />
                            </Box>
                          </Flex>

                          <TextInput
                            key={uuidv4()}
                            fieldname={`djt[${index}].djtNotes`}
                            fieldvalue={item.djtNotes || ''}
                            register={register}
                            errors={errors}
                            fieldtype='hidden'
                          />
                        </React.Fragment>
                      );
                    })}

                    <Flex>
                      <Box />
                      <Spacer />
                      <Box w={'25%'} mt={5}>
                        <TextInput
                          fieldname='TotalAmount'
                          fieldlabel="Total"
                          register={register}
                          errors={errors}
                          textAlign='right'
                          isRequired={true} 
                          isReadOnly={true}
                          defaultValue='$0.00'
                        />
                      </Box>
                    </Flex>

                    <Box w={'40%'}>
                      <TextareaInput
                        fieldname="customerMemo"
                        fieldlabel="Customer Note"
                        prettyname="Customer Note"
                        register={register}
                        errors={errors}
                        defaultvalue={'Thank you for your business.'}
                        rows={3}
                      />
                    </Box>
                  </Box>
                  
                  <HStack my={'25px'} key={uuidv4()}>
                    <Box w={'40%'}>
                      <ButtonSecondaryPlain 
                        width={'100%'}
                        name='cancel'
                        value='Cancel'
                        onClick={() =>  handleInvoiceReset()}
                        isDisabled={!isFormLoaded}
                      />  
                    </Box>
                    

                    <Box w={'60%'}>
                      <ButtonPrimaryPlain
                        type="submit"
                        onClick={handleSubmit(formSubmit, onError)}
                        width={'100%'}
                        name='submit-invoice'
                        value='Submit & Email Invoice via QuickBooks'
                        isDisabled={isSubmitDisabled}
                      />
                    </Box>
                  </HStack>
                </ModuleBox>
              </>
            )}

            {(!isInvoiceDJTVisible && lastQbInvoice) && (
              <ModuleBox>
                <Box>
                  <Text as="span" textStyle="dark-heading-2">
                    Invoice Created Successfully
                  </Text>

                  <Text mt={2}>
                    Invoice #: {lastQbInvoice.docNumber || lastQbInvoice.id}
                  </Text>

                  <Box mt={3}>
                    <a
                      href={lastQbInvoice.link}
                      target="_blank"
                      rel="noreferrer"
                      style={{ color: 'green', textDecoration: 'underline' }}
                    >
                      Open in QuickBooks
                    </a>
                  </Box>

                  <Box mt={6}>
                    <ButtonPrimaryPlain
                      type="button"
                      name="backToDjts"
                      value="Back to Invoices"
                      onClick={handleInvoiceReset}
                    />
                  </Box>
                </Box>
              </ModuleBox>
            )}
          </fieldset>
        </>
      </Container>

      <ModuleDrawer
        onClose={onViewDjtClose}
        isOpen={isViewDjtOpen}
        bodyContent={
          <ViewDailyJobTicket
            srcId={djtId}
            onClose={onViewDjtClose}
            drawerCloseButton={CustomCloseDrawerButton('view')}
          />
        }
        size={'full'}
      />

      <CenteredStatusProgressModal
        isModalOpen={isStatusModalOpen}
        onModalClose={onStatusModalClose}
        content={statusModalContent}
        closeButtonDisabled={statusModalCloseDisabled}
        handleSubmitModalClosed={handleStatusModalClosed}
        progress={statusProgress}
        successMessage={successMessage}
      />
    </>
  )
};
